import { cnx_clone, cnx_dot } from "../utils/cn_utilities";
import { cn_balcony_type } from "./cn_balcony_type";
import { cn_fence_type } from "./cn_fence_type";
import { cn_storey } from "./cn_storey";
import { cn_wall_interface } from "./cn_wall_interface";
import { cn_wall_type } from "./cn_wall_type";

export class cn_wall_metrics {

    /**
     *
     * @param {cn_wall_interface} wall_interface
     * @param {cn_storey} storey
     */
    constructor(wall_interface, storey) {
        this.height = 0;
        /** Height from bounding box (for walls) */
        this.height_bb = 0;
        /** Height from wall type (for balconies & fences) */
        this.height_type = 0;
        /** Height from wall type -> for fences, only railing height */
        this.height_type_fence = 0;
        /** Hottest side length (shall be interior) */
        this.side_length_1 = 0;
        /** Coldest side length (shall be exterior) */
        this.side_length_2 = 0;
        this.axis_length = 0;
        this.thickness = 0;

        this.gross_footprint_area = 0;
        this.net_footprint_area = 0;

        this.gross_side_area_1 = 0;
        this.net_side_area_1 = 0;
        this.gross_side_area_2 = 0;
        this.net_side_area_2 = 0;

        this.gross_side_exchange_area = 0;
        this.net_side_exchange_area = 0;

        this.gross_volume = 0;
        this.net_volume = 0;

        const s0 = (wall_interface.get_flow_direction()) ? 0 : 1;
        const s1 = 1 - s0;


        const solid_wall = wall_interface.build_3d_solid(storey, false);

        const wall_normal = cnx_clone(wall_interface.wall.bounds.normal);
        const solid_wall_faces = (solid_wall) ? solid_wall.get_faces() : [];
        let f1 = solid_wall_faces.find(f => cnx_dot(wall_normal, f.get_normal()) > 0.99);
        let f0 = solid_wall_faces.find(f => cnx_dot(wall_normal, f.get_normal()) < -0.99);
        const polygons = [null, null];
        polygons[0] = (f0) ? f0.clone() : wall_interface.build_side_polygon(0, storey);
        polygons[1] = (f1) ? f1.clone() : wall_interface.build_side_polygon(1, storey);

        const exchange_polygon = polygons[0].clone();
        exchange_polygon.intersects(polygons[1]);

        const wall = wall_interface.wall;
        const wall_type = wall.wall_type;

        // Base dimensions
        this.height_bb = this.height = solid_wall ? solid_wall.get_bounding_box().size[2] : 0;
        if (wall.wall_type.constructor == cn_balcony_type) {
            this.height_type = wall.wall_type.height;
            this.height = this.height_type;
        } else if (wall.wall_type.constructor == cn_fence_type) {
            this.height_type = wall.wall_type.wall_height;
            this.height_type_fence = wall.wall_type.fence_height;
            this.height = this.height_type + this.height_type_fence;
        }

        const wall_dir = cnx_clone(wall_interface.wall.bounds.direction);
        const wall_lengths = polygons.map((pg, idx) => {
            let xmin = 1;
            let xmax = 0;
            pg.contour_vertices.forEach(p => {
                const x = cnx_dot(wall_dir, p);
                if (xmin > xmax) {
                    xmin = x;
                    xmax = x;
                }
                else if (x < xmin) xmin = x;
                else if (x > xmax) xmax = x;
            });
            let xseg = [0, 1].map(nv => cnx_dot(wall_dir, wall_interface.space_segments[idx].points[nv]));
            xseg.sort((a, b) => a - b);
            if (xmin >= xseg[1] || xmax <= xseg[0]) return 0;
            xmin = Math.max(xmin, xseg[0]);
            xmax = Math.min(xmax, xseg[1]);
            return xmax - xmin;
        });
        this.side_length_1 = wall_lengths[s0];
        //wall_interface.get_length(s0);
        this.side_length_2 = wall_lengths[s1];

        this.thickness = wall_interface.wall.wall_type.thickness;

        // Footprint areas
        let footprint = wall_interface.footprint.get_area();
        this.gross_footprint_area = footprint;
        wall_interface.wall.openings.filter(op => op.valid && op.get_z() === 0).forEach(op => footprint -= wall_interface.wall.wall_type.thickness * op.get_width());
        this.net_footprint_area = footprint;

        if (wall.wall_type.constructor == cn_fence_type) {
            this.gross_side_area_1 = this.side_length_1 * this.height_type;
            this.gross_side_area_2 = this.side_length_2 * this.height_type;
            this.gross_side_area_fence_1 = this.side_length_1 * this.height_type_fence;
            this.gross_side_area_fence_2 = this.side_length_2 * this.height_type_fence;

            this.net_side_area_1 = this.gross_side_area_1;
            this.net_side_area_2 = this.gross_side_area_2;
            this.net_side_area_fence_1 = this.gross_side_area_fence_1;
            this.net_side_area_fence_2 = this.gross_side_area_fence_2;
            wall.openings.filter(op => op.valid).forEach(op => {
                const z0 = op.get_z();
                const dz_wall = (z0 < this.height_type) ? this.height_type - z0 : 0;
                this.net_side_area_1 -= dz_wall * op.get_width();
                this.net_side_area_2 -= dz_wall * op.get_width();
                const dz_fence = (z0 > this.height_type + this.height_type_fence) ? 0 : (z0 > this.height_type) ? this.height_type_fence - (z0 - this.height_type) : this.height_type_fence;
                this.net_side_area_fence_1 -= dz_fence * op.get_width();
                this.net_side_area_fence_2 -= dz_fence * op.get_width();
            });
        }
        else {
            // Gross sides areas
            this.gross_side_area_1 = polygons[s0].get_area();
            this.gross_side_area_2 = polygons[s1].get_area();
            this.gross_side_exchange_area = exchange_polygon.get_area();

            // Net sides areas
            wall.remove_openings_from_polygon(polygons[0], storey);
            wall.remove_openings_from_polygon(polygons[1], storey);
            wall.remove_openings_from_polygon(exchange_polygon, storey);
            this.net_side_area_1 = polygons[s0].get_area();
            this.net_side_area_2 = polygons[s1].get_area();
            this.net_side_exchange_area = exchange_polygon.get_area();
        }
        // Volumes
        const net_volume = (wall.wall_type.constructor == cn_wall_type) ? wall.build_3d_solid(storey, true) : null;
        this.gross_volume = (solid_wall) ? solid_wall.get_volume() : this.gross_footprint_area * (this.height_type + this.height_type_fence);
        this.net_volume = net_volume ? net_volume.get_volume() : this.net_footprint_area * (this.height_type + this.height_type_fence);

    }

}
